import { z } from 'zod';

export const operationStatusTypeValues = [
  'operations',
  'waiting',
  'flagged',
  'completed',
] as const;

export const operationStatusTypeSchema = z.enum(operationStatusTypeValues);

export type OperationStatusType = z.infer<typeof operationStatusTypeSchema>;

export const operationStatusItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  color: z.string().nullable(),
  statusType: operationStatusTypeSchema.nullable(),
  statusCode: z.string().nullable(),
  /**
   * The status's position in the operation status process. Nullable, and
   * treated as "not in the process" when absent — the process board is what
   * assigns it, and a catalog that has never opened that board has none.
   */
  operationStage: z.number().nullable(),
});

export type OperationStatusItem = z.infer<typeof operationStatusItemSchema>;

export const createOperationStatusInput = z.object({
  title: z.string().min(1),
  color: z.string().optional(),
  statusType: operationStatusTypeSchema.optional(),
  /**
   * Asked for on create so a status added after the process was configured is
   * not invisibly stranded outside it. Omitted means "not in the process".
   */
  operationStage: z.number().int().optional(),
});

export type CreateOperationStatusInput = z.infer<
  typeof createOperationStatusInput
>;

export const updateOperationStatusInput = z.object({
  id: z.uuid(),
  title: z.string().min(1).optional(),
  color: z.string().nullable().optional(),
  statusType: operationStatusTypeSchema.nullable().optional(),
  operationStage: z.number().int().nullable().optional(),
});

export type UpdateOperationStatusInput = z.infer<
  typeof updateOperationStatusInput
>;

export const findOperationStatusesInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  statusType: operationStatusTypeSchema.optional(),
  sort: z.enum(['title', 'color', 'statusCode', 'statusType']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

export type FindOperationStatusesInput = z.infer<
  typeof findOperationStatusesInput
>;

export const findOperationStatusesResultSchema = z.object({
  data: z.array(operationStatusItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindOperationStatusesResult = z.infer<
  typeof findOperationStatusesResultSchema
>;

export const deleteOperationStatusInput = z
  .object({
    id: z.uuid(),
    switchToId: z.uuid(),
  })
  .refine((input) => input.id !== input.switchToId, {
    message: 'Cannot reassign deleted status to itself',
    path: ['switchToId'],
  });

export type DeleteOperationStatusInput = z.infer<
  typeof deleteOperationStatusInput
>;

export const deleteOperationStatusResultSchema = z.object({
  id: z.uuid(),
  reassignedItemCount: z.number().int().nonnegative(),
});

export type DeleteOperationStatusResult = z.infer<
  typeof deleteOperationStatusResultSchema
>;

/**
 * Operation status codes that mean "this service is finished" for the purposes
 * of the auto-finalize (XT-757 / TCO-OPS01-076).
 *
 * WHY THIS IS NOT `status_type = 'completed'` ALONE. That was the original
 * predicate, and it never fired in practice: in every seeded organization the
 * only `completed`-typed statuses are S90 (Done) and S91 (Cancelled), while the
 * status operatives actually work a delivered service to is **S7: Delivered**,
 * which is typed `operations`. An itinerary whose every service reached S7 was
 * therefore never "complete", so it stayed in RESERVATIONS OK / ONGOING
 * forever and consultants finalized it by hand.
 *
 * S7 is terminal in the business sense the operations team means by it — the
 * service was delivered, there is nothing further to do. The UI has always
 * agreed (`subitem-button-color` renders S7/S90/S91 alike as finished), so this
 * aligns the auto-finalize with what the screen already shows.
 *
 * Keyed on `status_code`, never the title: titles are org-authored free text
 * ("S7: Delivered", "Entregue", "O7 Delivered"), while the code is the stable
 * identifier — the same reasoning as `FINALIZED_TC_STATUS` on the itinerary
 * side.
 */
export const FINISHED_OPERATION_STATUS_CODES = ['S7', 'S90', 'S91'] as const;

/**
 * Does a service carrying this operation status count as finished?
 *
 * `status_type` is compared case-insensitively: the column is plain text with
 * no constraint and the data carries both casings — the zod enum is lowercase
 * while `organization-init` seeds `'OPERATIONS'` — so a case-sensitive check
 * would finalize for some orgs and not others.
 *
 * A service with NO operation status at all is NOT finished. It is incomplete,
 * not absent: treating a null as vacuously complete would finalize an itinerary
 * whose services were never worked at all.
 */
export const isFinishedOperationStatus = (status: {
  statusCode: string | null;
  statusType: string | null;
}): boolean => {
  if (
    status.statusCode != null &&
    (FINISHED_OPERATION_STATUS_CODES as readonly string[]).includes(
      status.statusCode
    )
  ) {
    return true;
  }
  return status.statusType?.toLowerCase() === 'completed';
};
